import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';

import type { RobotHop, RobotLoop, RobotPathResult } from '../../../services/paths/robotPath/types';
import {
  BRIDGE_AUTO_COLOR,
  BRIDGE_AUTO_FILL,
  BRIDGE_MANUAL_COLOR,
  BRIDGE_MANUAL_FILL,
  type SmartPathBridge,
} from '../../../services/paths/bridges';
import { convertProjection4329 } from '../../../utils';
import { HIDE_AT_ZOOM_ZONE } from './zoomThresholds';

/** The driven polyline (same blue as the old local Dubin's overlay). */
export const ROBOT_PATH_COLOR = '#00BFFF';
/** A hop the drive model does not clear: too close to the boundary, or through an unsafe zone. */
export const ROBOT_PATH_FLAGGED_COLOR = '#FFA500';
export const ROBOT_PATH_LOOP_COLOR = '#FF0000';

/** Off until the mapmaker asks for it — the fetch costs a pathfinder round trip. */
export const ROBOT_PATH_DEFAULT_VISIBLE = false;

/** Coalesce the re-fetches that a burst of path edits would otherwise trigger. */
export const ROBOT_PATH_REFRESH_DEBOUNCE_MS = 500;

const Z_INDEX_ROBOT_PATH = 0; // Below PathLayer (Z_INDEX_PATH=1) so the current path stays on top.
const Z_INDEX_BRIDGE = -1; // Under the path: a bridge explains the route, it is not the route.

export type LoopMarker = {
  /** [lon, lat] of the waypoint the looping hop ends on. */
  lonLat: [number, number];
  label: string;
};

/**
 * The avoidable loops of a result as map markers.
 *
 * Necessary loops draw nothing: both endpoint headings are fixed by grid cores,
 * so the loop is unavoidable and there is nothing for the mapmaker to fix
 * (spec §5.4). Each marker sits on the waypoint its hop drives to.
 */
export function buildLoopMarkers(loops: RobotLoop[], hops: RobotHop[]): LoopMarker[] {
  const markers: LoopMarker[] = [];

  for (const loop of loops) {
    if (loop.kind !== 'avoidable') {
      continue;
    }

    const hop = hops.find((candidate) => candidate.fromIndex === loop.fromIndex && candidate.toIndex === loop.toIndex);
    const end = hop?.polyline[hop.polyline.length - 1];

    if (!end) {
      continue;
    }

    markers.push({ lonLat: [end[0], end[1]], label: `${loop.turnDeg}°` });
  }

  return markers;
}

/**
 * How far outside the boundary a flagged hop must get before the caption names
 * the number. The machine's own footprint — the arm offset the plan is drawn
 * through — is ~1 m, so a route that hugs the field grazes by about that much
 * as a matter of course; only a deeper excursion is worth saying out loud.
 */
export const ROBOT_PATH_DEPTH_CAPTION_M = 1;

/**
 * Caption for the overlay: what the robot drives and what is wrong with it.
 * A necessary loop is not wrong (nothing can be done about it), so it does not
 * appear; a hop outside the boundary does, with how far out the worst one gets
 * when that is more than the machine's own footprint.
 */
export function robotPathCaption(result: RobotPathResult): string {
  const parts = [`Robot path ${Math.round(result.totalLengthM)} m`];

  const avoidableLoops = result.loops.filter((loop) => loop.kind === 'avoidable').length;

  if (avoidableLoops > 0) {
    parts.push(avoidableLoops === 1 ? '1 loop' : `${avoidableLoops} loops`);
  }

  const hopsOffBounds = result.hops.filter((hop) => !hop.inBounds);

  if (hopsOffBounds.length > 0) {
    parts.push(hopsOffBounds.length === 1 ? '1 hop flagged' : `${hopsOffBounds.length} hops flagged`);

    const worstDepthM = Math.max(...hopsOffBounds.map((hop) => hop.outsideDepthM ?? 0));

    if (worstDepthM >= ROBOT_PATH_DEPTH_CAPTION_M) {
      parts.push(`worst ${worstDepthM.toFixed(1)} m out`);
    }
  }

  return parts.join(' · ');
}

export type BridgeFeature = {
  key: string;
  /** Outer rings of the bridge, as lon/lat (the layer projects them). */
  rings: [number, number][][];
  color: string;
  fillColor: string;
};

/**
 * The bridges of the safe boundary, drawn under the robot path.
 *
 * The auto bridges are the ones `SmartPathBuilder` built at planning time to
 * rescue a mission point that ended up outside the prepared boundary; they are
 * unioned onto the boundary *after* the unsafe zones are subtracted, so a
 * bridge is walkable even where it crosses an UNSAFE zone. Nothing else in the
 * app draws them, which is exactly why a path through a zone looked impossible
 * (2026-10-13). Pull-in zones are drawn too, so the whole picture is in one
 * place while the overlay is on.
 */
export function buildBridgeFeatures(bridges: SmartPathBridge[]): BridgeFeature[] {
  const features: BridgeFeature[] = [];

  bridges.forEach((bridge, index) => {
    const rings = bridge.geometry.type === 'Polygon' ? bridge.geometry.coordinates : bridge.geometry.coordinates.flat();

    if (rings.length === 0) {
      return;
    }

    features.push({
      key: `robot-bridge-${bridge.kind}-${bridge.pointId ?? index}-${index}`,
      rings: rings.map((ring) => ring.map(([lon, lat]) => [lon, lat] as [number, number])),
      color: bridge.kind === 'auto' ? BRIDGE_AUTO_COLOR : BRIDGE_MANUAL_COLOR,
      fillColor: bridge.kind === 'auto' ? BRIDGE_AUTO_FILL : BRIDGE_MANUAL_FILL,
    });
  });

  return features;
}

interface RobotPathLayerProps {
  visible: boolean;
  /** What /path/simulate answered; null while off, fetching, or after a failure. */
  result: RobotPathResult | null;
  /**
   * The bridges of the safe boundary the last planning run used — the auto ones
   * it built and the mapmaker's pull-in zones (`getSmartPathBridges`). Drawn
   * under the path so a route through an UNSAFE zone can be explained.
   */
  bridges?: SmartPathBridge[];
}

/**
 * Renders what the robot's own drive model says the mission does: one line per
 * driven hop (c2c chords dashed, hops that do not clear the boundary margin or
 * cross an unsafe zone in orange) and a warning marker on every avoidable loop.
 *
 * The fetch lives in MapMakerMap (the overlay is off by default and refreshed
 * on path edits), so this layer is a pure renderer of `result`.
 */
export default class RobotPathLayer extends PureComponent<RobotPathLayerProps> {
  render() {
    const { result, visible, bridges } = this.props;

    if (!visible || !result) {
      return null;
    }

    const hopFeatures = result.hops
      .filter((hop) => hop.polyline.length >= 2)
      .map((hop, index) => (
        <feature.LineStringReact
          key={`robot-hop-${hop.fromIndex}-${hop.toIndex}-${index}`}
          coordinates={hop.polyline.map(([lon, lat]) => convertProjection4329([lon, lat]))}
          strokeOptions={{
            color: hop.inBounds ? ROBOT_PATH_COLOR : ROBOT_PATH_FLAGGED_COLOR,
            width: 4,
            // A core-to-core chord is driven as a straight line, not a curve.
            ...(hop.pathType === 'c2c' ? { lineDash: [8, 6] } : {}),
          }}
          zIndex={Z_INDEX_ROBOT_PATH}
          forceRefresh={true}
          hideAtZoom={HIDE_AT_ZOOM_ZONE}
        />
      ));

    const loopFeatures = buildLoopMarkers(result.loops, result.hops).map((marker) => (
      <feature.PointReact
        key={`robot-loop-${marker.lonLat[0]}-${marker.lonLat[1]}-${marker.label}`}
        coordinate={convertProjection4329(marker.lonLat)}
        circleOptions={{
          radius: 7,
          fillOptions: { color: ROBOT_PATH_LOOP_COLOR },
          strokeOptions: { color: 'white', width: 2 },
        }}
        textOptions={{
          text: marker.label,
          font: 'bold 13px Calibri,sans-serif',
          offsetY: -16,
          fillOptions: { color: '#fff' },
          strokeOptions: { color: ROBOT_PATH_LOOP_COLOR, width: 3 },
        }}
        zIndex={Z_INDEX_ROBOT_PATH + 1}
      />
    ));

    const bridgeFeatures = buildBridgeFeatures(bridges ?? []).map((bridge) => (
      <feature.PolygonReact
        key={bridge.key}
        // @ts-ignore react-openlayers PolygonReact takes raw 3857 ring coordinates
        coordinates={bridge.rings.map((ring) => ring.map((lonLat) => convertProjection4329(lonLat)))}
        fillOptions={{ color: bridge.fillColor }}
        strokeOptions={{ color: bridge.color, width: 2, lineDash: [6, 6] }}
        zIndex={Z_INDEX_BRIDGE}
        forceRefresh={true}
        hideAtZoom={HIDE_AT_ZOOM_ZONE}
      />
    ));

    const features = [...bridgeFeatures, ...hopFeatures, ...loopFeatures];

    if (features.length === 0) {
      return null;
    }

    return (
      // @ts-ignore - layer.VectorImage children typing
      <layer.VectorImage zIndex={Z_INDEX_ROBOT_PATH}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
